import { doc, getDoc, setDoc } from "firebase/firestore";
import type { CharacterActionKey } from "@/character/actions";
import { normalizeGridSpec } from "@/character/grid";
import type { SkillBarState } from "@/character/skills/types";
import { isFirebaseConfigured } from "@/auth/firebase";
import { getCloudFirestore } from "@/cloud/firebase-app";
import { PETS_FIRESTORE_DOC } from "@/pet/constants";
import {
  createEmptyPetState,
  defaultPetSkillBar,
  type PetDefinition,
  type PetPlayerState,
} from "@/pet/types";
import { emptyAllocation, STAT_IDS } from "@/game/model/character-stats";
import { normalizeSkillBarFromCloud } from "@/pet/skills/normalize-skill-bar";

function privateDocRef(userId: string) {
  const db = getCloudFirestore();
  if (!db) throw new Error("Firebase ยังไม่ตั้งค่า");
  return doc(db, "users", userId, "private", PETS_FIRESTORE_DOC);
}

function normalizePet(raw: unknown): PetDefinition | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Partial<PetDefinition>;
  if (typeof s.id !== "string" || typeof s.name !== "string" || typeof s.createdAt !== "number") {
    return null;
  }
  if (typeof s.spritesheetDataUrl !== "string" || !s.spritesheetDataUrl) return null;
  if (!s.grid || typeof s.grid.frameWidth !== "number") return null;
  const actions = s.actions as Record<string, string> | undefined;
  if (!actions?.idle) return null;
  const normalizedActions: Record<CharacterActionKey, string> = {
    idle: actions.idle,
    run: actions.run ?? actions.idle,
    sit: actions.sit ?? actions.idle,
    attack: actions.attack ?? actions.idle,
  };
  const allocation = emptyAllocation();
  const rawAlloc = s.statAllocation as Record<string, number> | undefined;
  if (rawAlloc) {
    for (const id of STAT_IDS) {
      const n = rawAlloc[id];
      if (typeof n === "number" && Number.isFinite(n) && n > 0) {
        allocation[id] = Math.floor(n);
      }
    }
  }
  const skillBar: SkillBarState =
    normalizeSkillBarFromCloud(s.skillBar) ?? defaultPetSkillBar();
  return {
    id: s.id,
    name: s.name.trim().slice(0, 48) || "สัตว์เลี้ยง",
    createdAt: s.createdAt,
    spritesheetDataUrl: s.spritesheetDataUrl,
    grid: normalizeGridSpec(s.grid),
    actions: normalizedActions,
    apCardsApplied: Math.max(0, Math.floor(s.apCardsApplied ?? 0)),
    sizeCardsApplied: Math.max(0, Math.floor(s.sizeCardsApplied ?? 0)),
    statAllocation: allocation,
    skillBar,
  };
}

function normalizeState(raw: unknown): PetPlayerState {
  const base = createEmptyPetState();
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Partial<PetPlayerState>;
  const pets: PetDefinition[] = [];
  if (Array.isArray(o.pets)) {
    for (const entry of o.pets) {
      const norm = normalizePet(entry);
      if (norm) pets.push(norm);
    }
  }
  const equippedId =
    typeof o.equippedId === "string" && pets.some((p) => p.id === o.equippedId)
      ? o.equippedId
      : null;
  return { version: 1, equippedId, pets };
}

export async function loadPetState(userId: string): Promise<PetPlayerState> {
  if (!isFirebaseConfigured()) return createEmptyPetState();
  try {
    const snap = await getDoc(privateDocRef(userId));
    if (!snap.exists()) return createEmptyPetState();
    return normalizeState(snap.data());
  } catch {
    return createEmptyPetState();
  }
}

export async function savePetState(userId: string, state: PetPlayerState): Promise<void> {
  if (!isFirebaseConfigured()) {
    throw new Error("ต้องตั้งค่า Firebase ก่อนบันทึกสัตว์เลี้ยง");
  }
  const normalized = normalizeState(state);
  await setDoc(privateDocRef(userId), normalized);
}

export function newPetId(): string {
  return `pet_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function equippedPet(state: PetPlayerState): PetDefinition | null {
  if (!state.equippedId) return null;
  return state.pets.find((p) => p.id === state.equippedId) ?? null;
}

export function updatePetInState(state: PetPlayerState, pet: PetDefinition): PetPlayerState {
  const idx = state.pets.findIndex((p) => p.id === pet.id);
  if (idx < 0) return state;
  const pets = [...state.pets];
  pets[idx] = pet;
  return { ...state, pets };
}

export function removePetFromState(state: PetPlayerState, petId: string): PetPlayerState {
  if (!state.pets.some((p) => p.id === petId)) return state;
  const pets = state.pets.filter((p) => p.id !== petId);
  const equippedId = state.equippedId === petId ? null : state.equippedId;
  return { ...state, pets, equippedId };
}
